import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PaginationBar,
} from '@/components/ui'
import { IconRefresh } from '@/components/icons'
import { api } from '@/lib/api'
import { formatDateTimeDot, ticketPathSegmentFromListItem, ticketPublicId } from '@/lib/ticket-display'
import type { FailedDeliveriesResponse, FailedDeliveryItem } from '@/types/api'

const PAGE_SIZE = 25

function statusLabel(status: string): string {
  if (status === 'failed') return 'Ошибка'
  if (status === 'skipped') return 'Пропущено'
  if (status === 'pending') return 'В очереди'
  if (status === 'sent') return 'Доставлено'
  return status || '—'
}

function statusColor(status: string): string {
  if (status === 'failed') return 'red'
  if (status === 'skipped') return 'yellow'
  if (status === 'pending') return 'blue'
  if (status === 'sent') return 'green'
  return 'gray'
}

function channelLabel(item: FailedDeliveryItem): string {
  if (!item.channel) return '—'
  return `${item.channel.name} · ${item.channel.channel_type}`
}

export function FailedDeliveriesPanel() {
  const [page, setPage] = useState(1)
  const [data, setData] = useState<FailedDeliveriesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.failedDeliveries({ page, page_size: PAGE_SIZE })
      setData(res)
      if (res.page !== page) setPage(res.page)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof Error ? e.message : 'Не удалось загрузить недоставленные сообщения',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    void load()
  }, [load])

  const retry = useCallback(
    async (messageId: string) => {
      setRetryingId(messageId)
      try {
        await api.retryFailedDelivery(messageId)
        showNotification({
          title: 'Поставлено в очередь',
          message: 'Сообщение будет отправлено повторно',
          color: 'green',
        })
        await load()
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof Error ? e.message : 'Не удалось повторить отправку',
          color: 'red',
        })
      } finally {
        setRetryingId(null)
      }
    },
    [load],
  )

  const pageCount = useMemo(() => Math.max(1, data?.pages ?? 1), [data?.pages])

  if (loading && !data) {
    return (
      <PageCenterLoader label="Загружаем недоставленные сообщения…" />
    )
  }

  return (
    <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={700} size="lg">Недоставленные сообщения операторов</Text>
            <Text size="sm" c="dimmed">
              Показываются исходящие ответы операторов со статусом ошибки доставки или пропуска.
            </Text>
          </Stack>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={loading}
            onClick={() => void load()}
          >
            Обновить
          </Button>
        </Group>

        <Card withBorder radius="md" p={0}>
          {!loading && (data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Недоставленных сообщений нет"
              description="Ошибки доставки ответов оператора здесь появятся автоматически."
            />
          ) : (
            <DataTableScroll>
              <DataTable style={{ minWidth: 1180 }}>
                <DataThead>
                  <DataTr>
                    <DataTh>Проект</DataTh>
                    <DataTh>Обращение</DataTh>
                    <DataTh>Сообщение</DataTh>
                    <DataTh>Дата и время</DataTh>
                    <DataTh>Канал</DataTh>
                    <DataTh>Статус</DataTh>
                    <DataTh>Причина</DataTh>
                    <DataTh align="right">Действие</DataTh>
                  </DataTr>
                </DataThead>
                <DataTbody>
                  {(data?.items ?? []).map((item) => {
                    const ticketRef = ticketPathSegmentFromListItem({
                      id: item.ticket_id,
                      ticket_number: item.ticket_number,
                      project_slug: item.project.slug,
                    })
                    return (
                      <DataTr key={item.message_id}>
                        <DataTd>
                          <Text size="sm" fw={600}>{item.project.name}</Text>
                        </DataTd>
                        <DataTd>
                          <Text
                            component={Link}
                            to={`/tickets/${ticketRef}`}
                            size="sm"
                            fw={600}
                            style={{ textDecoration: 'none' }}
                          >
                            {ticketPublicId(item.project.slug, item.ticket_number)}
                          </Text>
                        </DataTd>
                        <DataTd style={{ maxWidth: 360 }}>
                          <Text size="sm" lineClamp={3}>{item.content || '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm">{item.created_at ? formatDateTimeDot(item.created_at) : '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Text size="sm">{channelLabel(item)}</Text>
                        </DataTd>
                        <DataTd>
                          <Badge color={statusColor(item.channel_delivery_status)} variant="light">
                            {statusLabel(item.channel_delivery_status)}
                          </Badge>
                        </DataTd>
                        <DataTd style={{ maxWidth: 320 }}>
                          <Text size="sm" c="dimmed" lineClamp={3}>{item.reason}</Text>
                        </DataTd>
                        <DataTd align="right">
                          <Button
                            size="xs"
                            variant="light"
                            loading={retryingId === item.message_id}
                            disabled={retryingId != null && retryingId !== item.message_id}
                            onClick={() => void retry(item.message_id)}
                          >
                            Отправить повторно
                          </Button>
                        </DataTd>
                      </DataTr>
                    )
                  })}
                </DataTbody>
              </DataTable>
            </DataTableScroll>
          )}
        </Card>

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Всего: {data?.total ?? 0}
          </Text>
          <PaginationBar
            page={data?.page ?? page}
            pageCount={pageCount}
            total={data?.total ?? 0}
            disabled={loading}
            onPageChange={setPage}
          />
        </Group>
    </Stack>
  )
}

export function FailedDeliveriesPage() {
  return (
    <PageShell>
      <FailedDeliveriesPanel />
    </PageShell>
  )
}
